import { c as _c } from "react/compiler-runtime";
import { Avatar } from '@algoacademy/design-system/web/actions/Avatar';
import { LightCard } from '@algoacademy/design-system/web/data-display/Card';
import { DiscordLogo, GoogleLogo } from '@algoacademy/design-system/web/svg/logos';
import { ChatBubbleOvalLeftEllipsisIcon, StarIcon } from '@heroicons/react/24/solid';
import { cn } from '@/app/_lib/utils';
import type { Proof, ProofTone } from './data';
const AVATAR_TONE: Record<ProofTone, string> = {
  blue: 'bg-blue-100 text-blue-700',
  emerald: 'bg-emerald-100 text-emerald-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  rose: 'bg-rose-100 text-rose-700',
  violet: 'bg-violet-100 text-violet-700'
};
function getInitials(name: string) {
  return name.split(' ')
  // Skip punctuation and short conjunctions ("i") so e.g. "Anna i Marek" → AM.
  .filter(part => /^\p{L}{2,}/u.test(part)).map(part => part[0]).slice(0, 2).join('').toUpperCase();
}
function StarRating(t0) {
  const $ = _c(8);
  const {
    rating
  } = t0;
  const t1 = `Ocena ${rating} na 5`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [1, 2, 3, 4, 5];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  let t4;
  if ($[1] !== rating) {
    t3 = <div className="flex gap-0.5 text-amber-500" aria-hidden="true">{t2.map(star => <StarIcon key={star} className={cn("size-3.5", star > rating && "text-slate-200")} />)}</div>;
    t4 = <span className="text-contentTertiary text-xs">{rating},0</span>;
    $[1] = rating;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t1 || $[5] !== t3 || $[6] !== t4) {
    t5 = <div className="flex items-center gap-1" role="img" aria-label={t1}>{t3}{t4}</div>;
    $[4] = t1;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
function ProofAuthor(t0) {
  const $ = _c(21);
  const {
    proof,
    inverse: t1
  } = t0;
  const inverse = t1 === undefined ? false : t1;
  const t2 = AVATAR_TONE[proof.tone];
  let t3;
  if ($[0] !== t2) {
    t3 = cn("size-10 shrink-0", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== proof.author) {
    t4 = getInitials(proof.author);
    $[2] = proof.author;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = <Avatar className={t3} initials={t4} />;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = inverse && "text-white";
  let t7;
  if ($[7] !== t6) {
    t7 = cn("font-semibold text-sm", t6);
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== proof.author || $[10] !== t7) {
    t8 = <p className={t7}>{proof.author}</p>;
    $[9] = proof.author;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== inverse || $[13] !== proof.role) {
    t9 = proof.role ? <p className={cn("mt-0.5 text-contentTertiary text-xs leading-4", inverse && "text-slate-400")}>{proof.role}</p> : null;
    $[12] = inverse;
    $[13] = proof.role;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = <div className="min-w-0">{t8}{t9}</div>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t5) {
    t11 = <div className="flex min-w-0 items-center gap-3">{t5}{t10}</div>;
    $[18] = t10;
    $[19] = t5;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}
function ReviewCard(t0) {
  const $ = _c(19);
  const {
    proof,
    className
  } = t0;
  const textSize = proof.content.length > 700 ? "text-sm leading-6" : proof.content.length < 90 ? "text-lg leading-8" : "text-[15px] leading-6";
  const t1 = `Opinia ${proof.author}`;
  let t2;
  if ($[0] !== className) {
    t2 = cn("rounded-[14px] p-[22px] shadow-sm", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <GoogleLogo size={22} />;
    t4 = <p className="font-semibold text-contentPrimary text-sm">Recenzja w Google</p>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== proof.rating) {
    t5 = <div className="flex items-center gap-3">{t3}<div>{t4}<StarRating rating={proof.rating} /></div></div>;
    $[4] = proof.rating;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== textSize) {
    t6 = cn("mt-4 whitespace-pre-line text-pretty text-contentSecondary", textSize);
    $[6] = textSize;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== proof.content || $[9] !== t6) {
    t7 = <p className={t6}>{proof.content}</p>;
    $[8] = proof.content;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== proof) {
    t8 = <div className="mt-5 border-borderPrimary border-t pt-4"><ProofAuthor proof={proof} /></div>;
    $[11] = proof;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t1 || $[14] !== t2 || $[15] !== t5 || $[16] !== t7 || $[17] !== t8) {
    t9 = <LightCard role="article" aria-label={t1} className={t2}>{t5}{t7}{t8}</LightCard>;
    $[13] = t1;
    $[14] = t2;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
}
function ChatCard(t0) {
  const $ = _c(22);
  const {
    proof,
    className
  } = t0;
  const t1 = `Wiadomość ${proof.author} na Discordzie`;
  let t2;
  if ($[0] !== className) {
    t2 = cn("rounded-[14px] border border-slate-700 bg-slate-800 p-4 shadow-sm", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="mb-4 flex items-center gap-2"><span className="inline-flex items-center gap-1.5 rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 font-bold text-indigo-700 text-xs"><DiscordLogo size={13} />Discord</span></div>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== proof) {
    t4 = <ProofAuthor proof={proof} inverse={true} />;
    $[3] = proof;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== proof.author) {
    t5 = <span className="font-semibold text-[#93b4ff] text-sm">{proof.author}</span>;
    $[5] = proof.author;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== proof.timestamp) {
    t6 = <span className="text-slate-400 text-xs">{proof.timestamp}</span>;
    $[7] = proof.timestamp;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = <div className="mb-1 flex flex-wrap items-baseline gap-x-2">{t5}{t6}</div>;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== proof.content) {
    t8 = <p className="text-pretty text-slate-300 text-sm leading-6">{proof.content}</p>;
    $[12] = proof.content;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = <div className="mt-4 border-slate-700 border-t pt-4">{t7}{t8}</div>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t1 || $[18] !== t2 || $[19] !== t4 || $[20] !== t9) {
    t10 = <article aria-label={t1} className={t2}>{t3}{t4}{t9}</article>;
    $[17] = t1;
    $[18] = t2;
    $[19] = t4;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}
function SmsCard(t0) {
  const $ = _c(15);
  const {
    proof,
    className
  } = t0;
  const t1 = `Wiadomość SMS od ${proof.author}`;
  let t2;
  if ($[0] !== className) {
    t2 = cn("rounded-[14px] border border-borderPrimary bg-white p-4 shadow-sm", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex items-center gap-2"><span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 font-bold text-emerald-700 text-xs"><ChatBubbleOvalLeftEllipsisIcon className="size-3.5" />SMS</span></div>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== proof.content) {
    t4 = <div className="mt-4 w-fit max-w-full rounded-[18px] rounded-bl-[6px] bg-algoprimary px-4 py-3"><p className="whitespace-pre-line text-pretty text-sm text-white leading-6">{proof.content}</p></div>;
    $[3] = proof.content;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== proof.timestamp) {
    t5 = proof.timestamp ? <p className="mt-1.5 text-contentTertiary text-xs">{proof.timestamp}</p> : null;
    $[5] = proof.timestamp;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== proof) {
    t6 = <div className="mt-4 border-borderPrimary border-t pt-4"><ProofAuthor proof={proof} /></div>;
    $[7] = proof;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = <article aria-label={t1} className={t2}>{t3}{t4}{t5}{t6}</article>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
export function ProofCard(t0) {
  const $ = _c(9);
  const {
    proof,
    className
  } = t0;
  switch (proof.kind) {
    case "review":
      {
        let t1;
        if ($[0] !== className || $[1] !== proof) {
          t1 = <ReviewCard proof={proof} className={className} />;
          $[0] = className;
          $[1] = proof;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        return t1;
      }
    case "chat":
      {
        let t1;
        if ($[3] !== className || $[4] !== proof) {
          t1 = <ChatCard proof={proof} className={className} />;
          $[3] = className;
          $[4] = proof;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        return t1;
      }
    case "sms":
      {
        let t1;
        if ($[6] !== className || $[7] !== proof) {
          t1 = <SmsCard proof={proof} className={className} />;
          $[6] = className;
          $[7] = proof;
          $[8] = t1;
        } else {
          t1 = $[8];
        }
        return t1;
      }
    default:
      {
        return null;
      }
  }
}